// "Which smööts is right for you?" quiz funnel.
// <sm-quiz data-mode="overlay|page"><dialog data-quiz-panel>...</dialog></sm-quiz>
//
// Everything the wizard needs is already in the DOM or in two JSON blobs rendered by
// Liquid (sections/sm-quiz.liquid, blocks/_sm-quiz-question.liquid, _sm-quiz-answer.liquid,
// _sm-quiz-result.liquid): no fetch, deterministic, works the same every time for the
// same answers.
//
// ---------------------------------------------------------------------------------------
// RECOMMENDATION ENGINE (documented here, this is the whole thing)
// ---------------------------------------------------------------------------------------
// 1. Every answer card carries its own `data-scores` JSON, e.g. {"lean-dust": 5}. Selecting
//    an answer adds those points to a running per-product/stack score. That map is hand
//    tuned by the theme editor (David), this file only sums it.
// 2. Stacks never get points directly from an answer. Instead, once every answer is summed,
//    each stack's score is the sum of its member formulas' scores, but ONLY counted when at
//    least 2 of its members scored above zero (STACK_SYNERGY below). That is the literal
//    version of "recommend a stack when 2 goals fit it, otherwise the best single formula":
//    one strong goal lights up one formula; two goals that both belong to the same stack
//    light up the stack instead, because the stack now outscores either formula alone.
// 3. Two small, explicitly documented cross-question rules run after the sum:
//    - Coffee + weight: picking "coffee every day" and a weight-related goal gives
//      Skinny Coffee an extra boost (COFFEE_WEIGHT_BONUS), on top of its own answer score.
//    - Format tie-breaker: capsule formulas vs. dust/coffee formulas get a small bonus
//      (FORMAT_BONUS) when they match the shopper's capsule/powder preference. Small on
//      purpose: a stated preference should break ties, not override a strong goal match.
// 4. Eligibility is a hard filter, not a score penalty: Man Pills is removed from the
//    candidate list for "female", Moon Pills for "male" (and any stack a result block marks
//    exclude_male/exclude_female, i.e. Glow Stack for "male", Active Stack for "female",
//    since both are built around one of those two formulas). "Prefer not to say" excludes
//    nothing.
// 5. Whatever is left is sorted by score, ties broken by handle so the result is always the
//    same for the same answers. #1 is the hero result, the next two distinct candidates are
//    the alternatives.
// ---------------------------------------------------------------------------------------

import { cartAdd, emit, lockScroll, trapFocus } from './sm-core.js';

const STORAGE_KEY = 'sm:quiz:v1';

// Stack -> member formula handles. Used only to compute stack synergy (rule 2 above).
const STACKS = {
  'focus-stack': ['big-brain-pills', 'chill-dust'],
  'glow-stack': ['glow-pills', 'moon-pills'],
  'sleep-calm-stack': ['chill-dust', 'detox-pills'],
  'weightloss-stack': ['lean-dust', 'skinny-coffee', 'flow-pills'],
  'active-stack': ['man-pills', 'flex-dust', 'bloat-pills'],
};
const STACK_SYNERGY = 0.65; // share of the members' combined score a stack earns once >= 2 members score
const COFFEE_WEIGHT_BONUS = 3;
const FORMAT_BONUS = 1.5;
const CAPSULE_FORMULAS = ['moon-pills', 'flow-pills', 'bloat-pills', 'detox-pills', 'big-brain-pills', 'glow-pills', 'man-pills'];
const POWDER_FORMULAS = ['chill-dust', 'flex-dust', 'lean-dust', 'skinny-coffee'];
const DEFAULT_FALLBACK = 'chill-dust'; // used only if every candidate somehow scores 0

function safeJSON(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Mirrors snippets/sm-hl-words.liquid so the JS-built pill highlight matches the rest of
// the theme (one <span class="sm-hl"> per word, first/mid/last classes for a multi-word
// wrap, gradient key on --hl).
function hlWords(text, key) {
  const words = String(text || '').trim().split(' ').filter(Boolean);
  return words
    .map((w, i) => {
      const pos = words.length > 1 ? (i === 0 ? ' sm-hl--start' : i === words.length - 1 ? ' sm-hl--end' : ' sm-hl--mid') : '';
      return `<span class="sm-hl${pos}" style="--hl: var(--grad-${key}); --hl-n: ${words.length}; --hl-i: ${i}">${esc(w)}</span>`;
    })
    .join(' ');
}

class SmQuiz extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode === 'page' ? 'page' : 'overlay';
    this.dialog = this.querySelector('[data-quiz-panel]');
    this.body = this.querySelector('[data-quiz-body]');
    this.copy = safeJSON(this.querySelector('[data-quiz-copy]')?.textContent, {});
    this.results = {};
    this.querySelectorAll('script[data-quiz-result]').forEach((el) => {
      const data = safeJSON(el.textContent, null);
      if (data?.handle) this.results[data.handle] = data;
    });

    this.questions = [...this.querySelectorAll('[data-step="question"]')];
    this.steps = ['cover', ...this.questions.map((q) => q.dataset.qKey), ...(this.querySelector('[data-step="email"]') ? ['email'] : []), 'results'];
    this.stepEls = {
      cover: this.querySelector('[data-step="cover"]'),
      email: this.querySelector('[data-step="email"]'),
      results: this.querySelector('[data-step="results"]'),
    };
    this.resultsMount = this.querySelector('[data-quiz-results-mount]');

    this.answers = {}; // { questionKey: [answerKey, ...] }
    this.stepIndex = 0;
    this.plan = '120';
    this.heroKey = null;

    // wide answer grids (4+ cards) run 2-up from 750px; short ones (sex, coffee, format...)
    // stay 2-up too once there are more than 2 options, single column otherwise.
    this.questions.forEach((q) => {
      const opts = q.querySelector('[data-q-opts]');
      if (opts && opts.children.length > 2) opts.dataset.cols = '2';
      const type = q.dataset.qType;
      opts?.querySelectorAll('.sm-quiz__opt-input').forEach((input) => {
        input.type = type === 'multi' ? 'checkbox' : 'radio';
        input.name = `sm-quiz-${q.dataset.qKey}`;
      });
      opts?.addEventListener('change', (e) => this.onAnswer(q, e));
      q.querySelector('[data-q-next]')?.addEventListener('click', () => this.next());
    });

    this.querySelector('[data-quiz-start]')?.addEventListener('click', () => this.next());
    this.querySelector('[data-quiz-back]')?.addEventListener('click', () => this.back());
    this.querySelector('[data-quiz-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog && this.mode === 'overlay') this.close(); });
    this.dialog?.addEventListener('cancel', (e) => { if (this.mode === 'page') e.preventDefault(); });
    this.dialog?.addEventListener('close', () => { if (this.mode === 'overlay') lockScroll(false); });

    const emailForm = this.querySelector('[data-quiz-email-form] form');
    emailForm?.addEventListener('submit', (e) => this.onEmailSubmit(e, emailForm));
    this.querySelector('[data-quiz-email-skip]')?.addEventListener('click', () => this.finish());

    SmQuiz.registered.add(this);
    if (this.mode === 'page') {
      this.showForPage();
    } else {
      // ?quiz=<handle> shared link: open straight to that result on any page, no click needed
      const urlHandle = new URLSearchParams(location.search).get('quiz');
      if (urlHandle && this.results[urlHandle]) this.open();
    }
  }

  disconnectedCallback() {
    SmQuiz.registered.delete(this);
  }

  // ---- entry / exit -------------------------------------------------------------------

  showForPage() {
    const urlHandle = new URLSearchParams(location.search).get('quiz');
    if (urlHandle && this.results[urlHandle]) {
      this.renderResults(urlHandle, {});
      this.goTo('results');
      return;
    }
    const saved = this.loadState();
    if (saved?.resultKey && this.results[saved.resultKey]) {
      this.answers = saved.answers || {};
      this.plan = saved.plan || '120';
      this.renderResults(saved.resultKey, this.answers);
      this.goTo('results');
      return;
    }
    this.goTo('cover');
  }

  open() {
    const urlHandle = new URLSearchParams(location.search).get('quiz');
    if (urlHandle && this.results[urlHandle]) {
      this.dialog.showModal();
      lockScroll(true);
      this.renderResults(urlHandle, {});
      this.goTo('results');
      emit('sm:quiz:start', { source: 'share_link', handle: urlHandle });
      return;
    }
    const saved = this.loadState();
    this.dialog.showModal();
    lockScroll(true);
    this.releaseFocus = trapFocus(this.dialog);
    if (saved?.resultKey && this.results[saved.resultKey]) {
      this.answers = saved.answers || {};
      this.plan = saved.plan || '120';
      this.renderResults(saved.resultKey, this.answers);
      this.goTo('results');
    } else {
      this.goTo('cover');
    }
    emit('sm:quiz:start', {});
  }

  close() {
    this.releaseFocus?.();
    this.dialog.close();
  }

  // ---- navigation -----------------------------------------------------------------------

  goTo(stepKey) {
    this.stepIndex = this.steps.indexOf(stepKey);
    this.questions.forEach((q) => { q.hidden = q.dataset.qKey !== stepKey; });
    Object.entries(this.stepEls).forEach(([key, el]) => { if (el) el.hidden = key !== stepKey; });

    const isQuestion = this.questions.some((q) => q.dataset.qKey === stepKey);
    const progress = this.querySelector('[data-quiz-progress]');
    const back = this.querySelector('[data-quiz-back]');
    if (progress) progress.hidden = !isQuestion;
    if (back) back.hidden = this.stepIndex <= 0;
    if (isQuestion) {
      const qNum = this.questions.findIndex((q) => q.dataset.qKey === stepKey) + 1;
      const fill = this.querySelector('[data-quiz-bar-fill]');
      if (fill) fill.style.width = `${Math.round((qNum / this.questions.length) * 100)}%`;
      const label = this.querySelector('[data-quiz-progress-label]');
      if (label) label.textContent = (this.copy.progress || '').replace('__CUR__', qNum).replace('__TOT__', this.questions.length);
    }

    const activeEl = this.stepEls[stepKey] || this.questions.find((q) => q.dataset.qKey === stepKey);
    activeEl?.querySelector('h2, legend, .sm-quiz__q-heading, .sm-quiz__cover-heading, .sm-quiz__email-heading')?.setAttribute('tabindex', '-1');
    activeEl?.querySelector('h2, legend, .sm-quiz__q-heading, .sm-quiz__cover-heading, .sm-quiz__email-heading')?.focus?.({ preventScroll: true });
    this.body.scrollTop = 0;
  }

  next() {
    const nextKey = this.steps[this.stepIndex + 1];
    if (!nextKey) return;
    if (nextKey === 'struggle') this.filterStruggle();
    if (nextKey === 'results') { this.finish(); return; }
    this.goTo(nextKey);
  }

  back() {
    const prevKey = this.steps[this.stepIndex - 1];
    if (prevKey) this.goTo(prevKey);
  }

  // ---- answering ------------------------------------------------------------------------

  onAnswer(question, e) {
    const opt = e.target.closest('.sm-quiz__opt');
    if (!opt) return;
    const qKey = question.dataset.qKey;
    const type = question.dataset.qType;
    const max = Number(question.dataset.qMax || 2);
    const allOpts = [...question.querySelectorAll('.sm-quiz__opt')];

    if (type === 'multi') {
      allOpts.forEach((o) => o.classList.toggle('is-selected', o.querySelector('input').checked));
      const selected = allOpts.filter((o) => o.classList.contains('is-selected'));
      allOpts.forEach((o) => {
        const disable = selected.length >= max && !o.classList.contains('is-selected');
        o.classList.toggle('is-disabled', disable);
        o.querySelector('input').disabled = disable;
      });
      this.answers[qKey] = selected.map((o) => o.dataset.answerKey);
      const nextBtn = question.querySelector('[data-q-next]');
      if (nextBtn) { nextBtn.disabled = selected.length === 0; nextBtn.setAttribute('aria-disabled', String(selected.length === 0)); }
      if (selected.length) emit('sm:quiz:answer', { question: qKey, value: this.answers[qKey] });
    } else {
      allOpts.forEach((o) => o.classList.toggle('is-selected', o === opt));
      this.answers[qKey] = [opt.dataset.answerKey];
      emit('sm:quiz:answer', { question: qKey, value: this.answers[qKey] });
      clearTimeout(this._advanceTimer);
      this._advanceTimer = setTimeout(() => this.next(), 320);
    }
  }

  // struggle question: only show the option(s) whose for_goal matches a goal the shopper
  // picked in question 1 (up to 2 goals selected there); falls back to showing every
  // struggle option if, for any reason, nothing matches.
  filterStruggle() {
    const struggleQ = this.questions.find((q) => q.dataset.qKey === 'struggle');
    if (!struggleQ) return;
    const goals = this.answers.goal || [];
    const opts = [...struggleQ.querySelectorAll('.sm-quiz__opt')];
    let shown = 0;
    opts.forEach((o) => {
      const match = goals.includes(o.dataset.forGoal);
      o.hidden = !match;
      if (match) shown++;
    });
    if (!shown) opts.forEach((o) => { o.hidden = false; });
    // clear any previous selection made against a different goal set
    opts.forEach((o) => o.classList.remove('is-selected'));
    struggleQ.querySelectorAll('input').forEach((i) => { i.checked = false; });
    delete this.answers.struggle;
  }

  // ---- scoring engine ---------------------------------------------------------------------

  buildScores() {
    const score = {};
    Object.keys(this.results).forEach((k) => { score[k] = 0; });
    const flags = { sex: 'neutral', coffeeDrinker: false, format: null, pace: '120' };
    const answerLabels = {}; // qKey -> [{key, label}] for the "why it fits" bullets

    // Which distinct goal answers (question "goal" only) touch each stack's territory.
    // A stack only ever gets synergy credit when 2 different GOALS point at it (see below),
    // never from one goal's own primary + secondary formula, or from later questions
    // (struggle/lifestyle/...) just reinforcing a single goal's formula.
    const stackGoalHits = {};
    Object.keys(STACKS).forEach((s) => { stackGoalHits[s] = new Set(); });

    this.questions.forEach((q) => {
      const qKey = q.dataset.qKey;
      const picked = this.answers[qKey] || [];
      answerLabels[qKey] = [];
      picked.forEach((answerKey) => {
        const opt = q.querySelector(`.sm-quiz__opt[data-answer-key="${CSS.escape(answerKey)}"]`);
        if (!opt) return;
        answerLabels[qKey].push({ key: answerKey, label: opt.querySelector('.sm-quiz__opt-label')?.textContent || '' });
        const scores = safeJSON(opt.dataset.scores, {});
        Object.entries(scores).forEach(([handle, pts]) => { if (handle in score) score[handle] += Number(pts) || 0; });
        if (qKey === 'goal') {
          Object.entries(STACKS).forEach(([stack, members]) => {
            if (members.some((m) => (scores[m] || 0) > 0)) stackGoalHits[stack].add(answerKey);
          });
        }
        (opt.dataset.flags || '').split(',').map((f) => f.trim()).filter(Boolean).forEach((flag) => {
          if (flag === 'exclude_male' || flag === 'exclude_female') flags.sex = flag === 'exclude_male' ? 'female' : 'male';
          if (flag === 'coffee_drinker') flags.coffeeDrinker = true;
          if (flag === 'format_capsule') flags.format = 'capsule';
          if (flag === 'format_powder') flags.format = 'powder';
          if (flag === 'pace_30') flags.pace = '30';
          if (flag === 'pace_120') flags.pace = '120';
        });
      });
    });

    // rule: coffee drinker + a weight goal boosts Skinny Coffee specifically
    const goals = this.answers.goal || [];
    if (flags.coffeeDrinker && goals.includes('goal_weight') && 'skinny-coffee' in score) {
      score['skinny-coffee'] += COFFEE_WEIGHT_BONUS;
    }

    // rule: capsule/powder preference is a small tie-breaker on single formulas only
    if (flags.format === 'capsule') CAPSULE_FORMULAS.forEach((h) => { if (h in score) score[h] += FORMAT_BONUS; });
    if (flags.format === 'powder') POWDER_FORMULAS.forEach((h) => { if (h in score) score[h] += FORMAT_BONUS; });

    // stack synergy: a stack only scores once 2 DIFFERENT goals (question 1) point at it,
    // e.g. "weight" + "energy" both feeding Weightloss Stack. One goal alone (which may
    // still touch 2 of a stack's members through its own primary + secondary formula)
    // is deliberately not enough: that is the single-formula case.
    Object.entries(STACKS).forEach(([stack, members]) => {
      if (!(stack in score)) return;
      if (stackGoalHits[stack].size >= 2) {
        const memberSum = members.reduce((sum, m) => sum + (score[m] || 0), 0);
        score[stack] += memberSum * STACK_SYNERGY;
      }
    });

    // eligibility: hard exclude, never just a low score
    const eligible = {};
    Object.entries(score).forEach(([handle, pts]) => {
      const meta = this.results[handle];
      const restrict = meta?.restrict_sex || 'none';
      if (flags.sex === 'female' && (handle === 'man-pills' || restrict === 'exclude_female')) return;
      if (flags.sex === 'male' && (handle === 'moon-pills' || restrict === 'exclude_male')) return;
      eligible[handle] = pts;
    });

    return { score: eligible, flags, answerLabels };
  }

  rank() {
    const { score, flags, answerLabels } = this.buildScores();
    const ranked = Object.entries(score).sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
    let hero = ranked[0]?.[0];
    if (!hero || ranked[0][1] <= 0) hero = this.results[DEFAULT_FALLBACK] ? DEFAULT_FALLBACK : ranked[0]?.[0];
    const alternatives = ranked.filter(([h]) => h !== hero).slice(0, 2).map(([h]) => h);
    return { hero, alternatives, flags, answerLabels };
  }

  finish() {
    const { hero, alternatives, flags, answerLabels } = this.rank();
    this.plan = flags.pace === '30' ? '30' : '120';
    this.renderResults(hero, this.answers, alternatives, answerLabels);
    this.saveState(hero);
    this.goTo('results');
    emit('sm:quiz:complete', { result: hero, alternatives, plan: this.plan });
  }

  // ---- results rendering ------------------------------------------------------------------

  buildBullets(handle, answerLabels) {
    const copy = this.copy;
    const goalAnswers = answerLabels?.goal || [];
    const bullets = goalAnswers.slice(0, 2).map((a) => (copy.goalMatch || '').replace('__GOAL__', a.label.toLowerCase()));
    const item = this.results[handle];
    (item?.hero_bullets || []).forEach((b) => { if (bullets.length < 3) bullets.push(b); });
    if (bullets.length < 3 && copy.formatMatch) bullets.push(copy.formatMatch);
    if (bullets.length < 3 && copy.genericBullet) bullets.push(copy.genericBullet);
    return bullets.slice(0, 3);
  }

  priceBlock(item, plan) {
    const p = item.plans?.[plan];
    if (!p) return '';
    const save = p.compare > p.price ? Math.round(((p.compare - p.price) / p.compare) * 100) : 0;
    return `
      <div class="sm-quiz__price-row">
        <b class="sm-quiz__price-now">${esc(p.price_money)}</b>
        ${p.compare > p.price ? `<s class="sm-quiz__price-was">${esc(p.compare_money)}</s>` : ''}
        ${save > 0 ? `<span class="sm-quiz__price-save" style="--hl: var(--grad-${item.gradient_key})">${esc((this.copy.savePercent || '').replace('__N__', save))}</span>` : ''}
      </div>
      <div class="sm-quiz__price-day">${esc((this.copy.perDay || '').replace('__P__', p.per_day_money))}</div>`;
  }

  renderResults(handle, answers = {}, alternatives = [], answerLabels = null) {
    const item = this.results[handle];
    if (!item) return;
    this.heroKey = handle;
    const copy = this.copy;
    const bullets = this.buildBullets(handle, answerLabels);
    const alts = alternatives.filter((h) => h !== handle).slice(0, 2).map((h) => this.results[h]).filter(Boolean);

    this.resultsMount.innerHTML = `
      <div class="sm-quiz__result">
        <span class="sm-mono sm-quiz__result-eyebrow">${esc(copy.resultEyebrow)}</span>
        <h2 class="sm-quiz__result-heading">${hlWords(item.title, item.gradient_key)}</h2>
        ${item.tagline ? `<p class="sm-quiz__result-tagline">${esc(item.tagline)}</p>` : ''}
        <div class="sm-quiz__result-media" style="--hl: var(--grad-${item.gradient_key})">
          ${item.image ? `<img src="${esc(item.image.url)}" alt="${esc(item.image.alt)}" width="400" height="225" loading="eager">` : ''}
        </div>
        ${bullets.length ? `
          <div>
            <p class="sm-quiz__result-why-heading">${esc(copy.whyHeading)}</p>
            <ul class="sm-quiz__result-bullets" role="list">
              ${bullets.map((b) => `<li class="sm-quiz__result-bullet"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><span>${esc(b)}</span></li>`).join('')}
            </ul>
          </div>` : ''}

        <fieldset class="sm-quiz__plan-toggle" data-quiz-plan-toggle style="border:0;padding:0;margin:0;">
          <legend class="sm-visually-hidden">${esc(copy.planHeading)}</legend>
          ${['120', '30'].filter((p) => item.plans?.[p]).map((p) => `
            <label class="sm-quiz__plan-opt${this.plan === p ? ' is-selected' : ''}" data-plan-value="${p}">
              <input type="radio" name="sm-quiz-plan" value="${p}"${this.plan === p ? ' checked' : ''}>
              <span class="sm-quiz__plan-opt-label">${p === '120' ? esc(copy.plan120) : esc(copy.plan30)}${p === '120' ? `<span class="sm-quiz__plan-opt-badge">${esc(copy.mostPopular)}</span>` : ''}</span>
            </label>`).join('')}
        </fieldset>

        <div data-quiz-price>${this.priceBlock(item, this.plan)}</div>

        <div class="sm-quiz__cta-row">
          <button type="button" class="sm-btn sm-btn--primary sm-btn--l sm-btn--full" data-quiz-add>${esc(copy.addToCart)}</button>
          <a class="sm-quiz__view-link" href="${esc(item.url)}">${esc(copy.viewProduct)}</a>
        </div>

        ${alts.length ? `
          <div class="sm-quiz__alts">
            <p class="sm-quiz__alts-heading">${esc(copy.alternativesHeading)}</p>
            <div class="sm-quiz__alt-list">
              ${alts.map((a) => `
                <button type="button" class="sm-quiz__alt-card" data-quiz-alt="${esc(a.handle)}">
                  <span class="sm-quiz__alt-img" style="--alt-hl: var(--grad-${a.gradient_key})">${a.image ? `<img src="${esc(a.image.url)}" alt="${esc(a.image.alt)}" width="120" height="120" loading="lazy">` : ''}</span>
                  <span class="sm-quiz__alt-title">${esc(a.title)}</span>
                  <span class="sm-quiz__alt-price">${a.plans?.[this.plan] ? esc(a.plans[this.plan].price_money) : ''}</span>
                </button>`).join('')}
            </div>
          </div>` : ''}

        <button type="button" class="sm-quiz__retake" data-quiz-retake>${esc(copy.retake)}</button>
      </div>`;

    const currentAlts = alts.map((a) => a.handle).length ? [handle, ...alts.map((a) => a.handle)] : [handle, ...alternatives];
    this.resultsMount.querySelector('[data-quiz-plan-toggle]')?.addEventListener('change', (e) => {
      const label = e.target.closest('.sm-quiz__plan-opt');
      if (!label) return;
      this.plan = label.dataset.planValue;
      this.resultsMount.querySelectorAll('.sm-quiz__plan-opt').forEach((o) => o.classList.toggle('is-selected', o === label));
      this.resultsMount.querySelector('[data-quiz-price]').innerHTML = this.priceBlock(item, this.plan);
    });
    this.resultsMount.querySelector('[data-quiz-add]')?.addEventListener('click', (e) => this.addToCart(item, e.currentTarget));
    this.resultsMount.querySelectorAll('[data-quiz-alt]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const swapTo = btn.dataset.quizAlt;
        const rest = currentAlts.filter((h) => h !== swapTo && h !== handle);
        this.renderResults(swapTo, this.answers, [handle, ...rest], answerLabels);
        this.saveState(swapTo);
      });
    });
    this.resultsMount.querySelector('[data-quiz-retake]')?.addEventListener('click', () => this.retake());
  }

  async addToCart(item, btn) {
    const plan = item.plans?.[this.plan];
    if (!item.variant_id || btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    const original = btn.textContent;
    btn.textContent = this.copy.adding || original;
    try {
      const line = { id: item.variant_id, quantity: 1 };
      if (plan?.id) line.selling_plan = plan.id;
      await cartAdd([line]);
      btn.textContent = this.copy.added || original;
      emit('sm:cart:updated', {});
      emit('sm:cart:open', {});
      setTimeout(() => { if (this.mode === 'overlay') this.close(); }, 400);
    } catch {
      btn.textContent = original;
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  async onEmailSubmit(e, form) {
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    try {
      btn?.setAttribute('aria-busy', 'true');
      await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      const msg = document.createElement('p');
      msg.className = 'sm-quiz__email-msg';
      msg.setAttribute('role', 'status');
      msg.textContent = this.copy.emailSuccess || '';
      form.after(msg);
    } catch {
      // never block the reveal on a network hiccup
    } finally {
      btn?.removeAttribute('aria-busy');
      setTimeout(() => this.finish(), 500);
    }
  }

  retake() {
    localStorage.removeItem(STORAGE_KEY);
    this.answers = {};
    this.plan = '120';
    this.heroKey = null;
    this.questions.forEach((q) => {
      q.querySelectorAll('input').forEach((i) => { i.checked = false; i.disabled = false; });
      q.querySelectorAll('.sm-quiz__opt').forEach((o) => { o.classList.remove('is-selected', 'is-disabled'); o.hidden = false; });
      q.querySelector('[data-q-next]')?.setAttribute('disabled', 'true');
    });
    this.goTo('cover');
  }

  // ---- persistence ----------------------------------------------------------------------

  saveState(resultKey) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers: this.answers, plan: this.plan, resultKey, ts: Date.now() }));
    } catch { /* private mode / storage disabled: quiz still works, just does not persist */ }
  }

  loadState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
  }
}

SmQuiz.registered = new Set();
if (!customElements.get('sm-quiz')) customElements.define('sm-quiz', SmQuiz);

// Global entry points: header quiz pill, mobile menu quiz card, and any other link to
// "#quiz" or a /pages/quiz url, on any page, open the first quiz instance instead of
// navigating there. Falls through to a normal navigation if no quiz instance exists yet
// (e.g. it was removed from overlay-group.json) or before the module has loaded.
function findOverlayQuiz() {
  return [...SmQuiz.registered].find((el) => el.mode === 'overlay') || document.querySelector('sm-quiz');
}
function isQuizLink(a) {
  if (!a || !a.href) return false;
  if (a.hasAttribute('data-quiz-open')) return true;
  let url;
  try { url = new URL(a.href, location.href); } catch { return false; }
  return url.hash === '#quiz' || /\/pages\/quiz\/?$/.test(url.pathname);
}
document.addEventListener('click', (e) => {
  const a = e.target.closest('a[href], [data-quiz-open]');
  if (!isQuizLink(a)) return;
  const quiz = findOverlayQuiz();
  if (!quiz) return; // let the real /pages/quiz link work
  e.preventDefault();
  quiz.open();
});
